import React, {useMemo} from 'react';

import {PredicateOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {getInitialFeatureFlags} from '../../../store/featureFlags/featureFlagsSelectors';
import {FeatureFlagsMap} from '../../../store/featureFlags/featureFlagsState';
import {FeatureFlagsEnum} from '../../../store/featureFlags/featureFlagsTypes';
import {
  ClientRuleCondition,
  RuleCondition,
  RuleConditionTypesEnum,
  RuleEditorConditionsGroup,
  RuleEditorConditionsGroupsEnum,
} from '../../rules/ruleConditionModel';
import {DropdownGroup} from '../../rules/ruleEditor/dropdowns/multipleGroupsDropdownHelpers';
import {useBundleService} from '../../rules/ruleEditor/hooks/useBundleService';
import {RuleActionConditionGroupIcon} from '../../rules/ruleEditor/ruleConditionGroupIcon';
import {RuleConditionGroupTitle} from '../../rules/ruleEditor/ruleConditionGroupTitle';
import {RuleConditionProPill} from '../../rules/ruleEditor/ruleConditionProPill';
import {RuleIntegration} from '../../rules/ruleIntegrationModel';
import {RuleConditionDefinition} from '../../rules/ruleParameterModel';
import {useBaseEditorContext} from '../baseEditorContext';
import {EditorErrorsEnum} from '../commons/editorErrors';
import {useExpressionsContext} from '../expressions/expressionsContext';
import {ConditionsStateActions} from './conditionsState';

/*
 * Constants.
 */

const featureFlagsForConditions: Partial<Record<string, FeatureFlagsEnum>> = {
  [RuleConditionTypesEnum.DATE_IS]: FeatureFlagsEnum.RULE_CONDITION_DATE_IS,
};

const conditionExpressionsGroups: ReadonlyArray<RuleEditorConditionsGroup> = [
  {
    id: RuleEditorConditionsGroupsEnum.EXPRESSION,
    items: [RuleConditionTypesEnum.COMPARE_EXPRESSION_STRING],
  },
];

/*
 * Helpers.
 */

export function useConditionCardProps(
  conditions: ReadonlyArray<ReadonlyArray<ClientRuleCondition>>,
  getConditionDefinition: (condition: ClientRuleCondition) => RuleConditionDefinition | undefined,
  onUpdateCondition: ConditionsStateActions['onUpdateCondition'],
  onDeleteCondition: ConditionsStateActions['onDeleteCondition'],
  onValidation: (key: string, error?: EditorErrorsEnum) => void,
  shouldHideError?: PredicateOf<ClientRuleCondition>,
) {
  return useMemo(
    () =>
      conditions.map((orConditions, index) =>
        orConditions.map((condition) => ({
          condition,
          conditionDefinition: getConditionDefinition(condition),
          onUpdateCondition: (newCondition: RuleCondition) => {
            onUpdateCondition(
              index,
              (c) => c.clientId === condition.clientId,
              (c) => ({...newCondition, clientId: c.clientId}),
            );
          },
          onRemoveCondition: () => onDeleteCondition(index, (c) => c.clientId === condition.clientId),
          onValidation,
          shouldHideError: shouldHideError?.(condition),
        })),
      ),
    [conditions, getConditionDefinition, onValidation, shouldHideError, onUpdateCondition, onDeleteCondition],
  );
}

export function useConditionGroups(
  ruleIntegration: RuleIntegration,
  conditionGroups: ReadonlyArray<RuleEditorConditionsGroup>,
) {
  const {namespace} = useBaseEditorContext();
  const {hasAnyExpressions} = useExpressionsContext();

  const {unavailableConditions} = useBundleService();

  const featureFlags = useAppSelector(getInitialFeatureFlags);

  return useMemo(() => {
    const dropdownGroups = [...(hasAnyExpressions ? conditionExpressionsGroups : []), ...conditionGroups];

    return dropdownGroups.map((group) => {
      const dropdownGroup: DropdownGroup<RuleConditionDefinition> = {
        title: <RuleConditionGroupTitle id={group.id} />,
        icon: <RuleActionConditionGroupIcon id={group.id} />,
        items: group.items
          .map((item: RuleConditionTypesEnum) => ruleIntegration.ruleConditionIdMap[item])
          .filter(isDefined)
          .filter((item) => maybeFilterConditionDefinition(item, featureFlags))
          .filter((item) => isNotRestricted(item, namespace)),
        disabledItems: [...unavailableConditions]
          .map((item) => ruleIntegration.ruleConditionIdMap[item])
          .filter(isDefined),
        serializeItemForSearch: (item) => `${item.name} ${item.altName ? `${item.altName} ` : ''}${item.id}`, // Adding string id to search for better relevance
        renderItemLabel: (item) => (
          <>
            {item.altName || item.name}
            <RuleConditionProPill item={item} />
          </>
        ),
        renderItemTooltip: (item) => item.tooltip,
        getItemKey: (item) => item.name,
      };
      return dropdownGroup;
    });
  }, [
    hasAnyExpressions,
    conditionGroups,
    unavailableConditions,
    ruleIntegration.ruleConditionIdMap,
    featureFlags,
    namespace,
  ]);
}

// @TODO: Considering merging with the same helper from ruleEditorActions.tsx.
function maybeFilterConditionDefinition(item: RuleConditionDefinition, featureFlags: FeatureFlagsMap) {
  const featureFlag = featureFlagsForConditions[item.id];

  if (!featureFlag) {
    return true;
  }

  return featureFlags[featureFlag];
}

// @TODO: Considering merging with the same helper from ruleEditorActions.tsx.
function isNotRestricted({restricted}: RuleConditionDefinition, namespace: string) {
  const namespacePrefix = namespace === 'global' ? namespace : namespace.slice(0, 3);

  if (!restricted || restricted.length === 0) {
    return true;
  }

  return restricted.includes(namespacePrefix);
}
